import { Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { SubscriptionPlanConfig } from '@/types/subscription-plans';
import { formatPrice } from '@/types/subscription-plans';

interface SubscriptionTierCardProps {
  plan: SubscriptionPlanConfig;
  isCurrentPlan: boolean;
  onSelect: (plan: SubscriptionPlanConfig) => void;
  isLoading?: boolean;
  disabled?: boolean;
}

export function SubscriptionTierCard({
  plan,
  isCurrentPlan,
  onSelect,
  isLoading = false,
  disabled = false,
}: SubscriptionTierCardProps) {
  const handleSelect = () => {
    if (!isCurrentPlan && !disabled) {
      onSelect(plan);
    }
  };

  return (
    <Card
      data-testid={`plan-card-${plan.id}`}
      className={cn(
        'relative flex flex-col transition-shadow hover:shadow-md',
        plan.popular && 'border-primary shadow-md',
        isCurrentPlan && 'border-green-500 bg-green-50/50 dark:bg-green-950/20'
      )}
    >
      {plan.popular && !isCurrentPlan && (
        <Badge
          className="absolute -top-2.5 left-1/2 -translate-x-1/2"
          data-testid="popular-badge"
        >
          Most Popular
        </Badge>
      )}

      {isCurrentPlan && (
        <Badge
          variant="secondary"
          className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-green-500 text-white"
          data-testid="current-plan-badge"
        >
          Current Plan
        </Badge>
      )}

      <CardHeader className="text-center pb-2">
        <CardTitle className="text-xl">{plan.name}</CardTitle>
        <CardDescription>{plan.description}</CardDescription>
      </CardHeader>

      <CardContent className="flex-1 space-y-4">
        <div className="text-center">
          <span className="text-3xl font-bold">{formatPrice(plan.priceInCents)}</span>
          <span className="text-muted-foreground">/month</span>
        </div>

        <ul className="space-y-2">
          {plan.features.map((feature, index) => (
            <li
              key={index}
              className={cn(
                'flex items-center gap-2 text-sm',
                !feature.included && 'text-muted-foreground'
              )}
            >
              {feature.included ? (
                <Check className="h-4 w-4 text-green-500 shrink-0" />
              ) : (
                <X className="h-4 w-4 text-muted-foreground shrink-0" />
              )}
              <span>{feature.text}</span>
            </li>
          ))}
        </ul>
      </CardContent>

      <CardFooter>
        <Button
          data-testid={`select-plan-${plan.id}`}
          className="w-full"
          variant={plan.popular && !isCurrentPlan ? 'default' : 'outline'}
          onClick={handleSelect}
          disabled={isCurrentPlan || isLoading || disabled}
        >
          {isLoading ? (
            'Processing...'
          ) : isCurrentPlan ? (
            'Current Plan'
          ) : (
            'Select Plan'
          )}
        </Button>
      </CardFooter>
    </Card>
  );
}
